import React, { useMemo } from "react";
import { Box, Typography, Stack } from "@mui/material";

const SEVERITY_COLOURS = {
  critical: "#c00000",
  high: "#ff0000",
  medium: "#ffc000",
  low: "#39b549",
};

const normalise = (obj = {}) => ({
  critical: obj.Critical ?? obj.critical ?? 0,
  high: obj.High ?? obj.high ?? 0,
  medium: obj.Medium ?? obj.medium ?? 0,
  low: obj.Low ?? obj.low ?? 0,
});

// Fixed createArc function with properly scaled coordinates for 300x300 SVG
const createArc = (percentage, startPercentage) => {
  const startAngle = startPercentage * 3.6 - 90;
  const endAngle = (startPercentage + percentage) * 3.6 - 90;
  const largeArcFlag = percentage > 50 ? 1 : 0;

  // Properly scaled for 300x300 SVG
  const outerR = 80; // Reduced from 105
  const innerR = 50; // Reduced from 70

  const cx = 150; // Center for 300x300 SVG
  const cy = 150; // Center for 300x300 SVG

  const toXY = (angleDeg, radius) => {
    const rad = (angleDeg * Math.PI) / 180;
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
  };

  const [x1Outer, y1Outer] = toXY(startAngle, outerR);
  const [x2Outer, y2Outer] = toXY(endAngle, outerR);
  const [x1Inner, y1Inner] = toXY(startAngle, innerR);
  const [x2Inner, y2Inner] = toXY(endAngle, innerR);

  return `
    M ${x1Outer},${y1Outer} 
    A ${outerR},${outerR} 0 ${largeArcFlag} 1 ${x2Outer},${y2Outer}
    L ${x2Inner},${y2Inner}
    A ${innerR},${innerR} 0 ${largeArcFlag} 0 ${x1Inner},${y1Inner}
    Z
  `;
};

export default function DonutChartMUI({ singleRunSecurityMetrics }) {
  const {
    severityBreakdown,
    notFixedSeverityDistribution,
    truePositives = 0,
  } = singleRunSecurityMetrics ?? {};

  // Use the first available severity data source
  const activeSeverityData =
    severityBreakdown || notFixedSeverityDistribution || {};

  const data = useMemo(() => {
    const sev = normalise(activeSeverityData);
    return ["critical", "high", "medium", "low"].map((key) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      value: sev[key],
      color: SEVERITY_COLOURS[key],
    }));
  }, [activeSeverityData]);

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const hasData = total > 0;

  // Create filtered data for chart (only non-zero values)
  const chartData = data.filter(({ value }) => value > 0);

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="center"
      justifyContent="center"
      width="100%"
      gap={{ xs: 2, sm: 3 }}
    >
      {/* Chart Container */}
      <Box display="flex" justifyContent="center" alignItems="center">
        {hasData ? (
          <svg width={300} height={300} style={{ transform: "rotate(-90deg)" }}>
            {(() => {
              let cumulative = 0;
              return chartData.map(({ value, color }, idx) => {
                const pct = (value / total) * 100;
                const d = createArc(pct, cumulative);
                cumulative += pct;
                return (
                  <path
                    key={idx}
                    d={d}
                    fill={color}
                    style={{ transition: "opacity .3s", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.target.style.opacity = 0.8)}
                    onMouseLeave={(e) => (e.target.style.opacity = 1)}
                  />
                );
              });
            })()}
            {/* Center hole adjusted for 300x300 SVG */}
            <circle cx={150} cy={150} r={45} fill="#fff" />

            {/* CENTER COUNT TEXT - Added here */}
            <text
              x="150"
              y="160"
              textAnchor="middle"
              fontSize="32"
              fill="#333"
              fontWeight="bold"
              style={{
                transform: "rotate(90deg)",
                transformOrigin: "150px 150px",
              }}
            >
              {total}
            </text>
          </svg>
        ) : (
          <Box
            width={300}
            height={300}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="subtitle1" color="text.secondary">
              No data
            </Typography>
          </Box>
        )}
      </Box>

      {/* Legend Container - Shows ALL severities including 0 values */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems={{ xs: "center", sm: "flex-start" }}
        minWidth={140}
      >
        {/* Removed the total from here since it's now in the center */}

        <Stack spacing={1}>
          {/* Display ALL severities, keeping original colors even for 0 values */}
          {data.map(({ key, label, value, color }) => (
            <Stack
              key={key}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 120 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: color, // Keep original color even for 0 values
                  opacity: value > 0 ? 1 : 0.5, // Slightly transparent for 0 values
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  color: value > 0 ? "text.primary" : "text.secondary", // Different color for 0 values
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  color: value > 0 ? "text.primary" : "text.secondary", // Different color for 0 values
                }}
              >
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
